import { datadogRum } from "@datadog/browser-rum";
import type { NextWebVitalsMetric } from "next/app";

import { isProduction } from "../utils/environment";
import { SERVICE_NAME } from "@zapier/utils";

const shouldTrackToDatadog = isProduction;

export function initializeDatadogRum() {
  // Don't initialize during SSR
  if (globalThis.window === undefined) {
    return;
  }

  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APP_ID;
  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;

  if (!applicationId || !clientToken) {
    console.info(
      `Datadog RUM not initialized: missing applicationId or clientToken.`,
    );
    return;
  }

  if (!shouldTrackToDatadog) {
    console.info(
      `Datadog RUM not initialized: not in a production environment.`,
    );
    return;
  }

  const faroCollectorUrl = process.env.NEXT_PUBLIC_FARO_COLLECTOR_URL;

  // See https://docs.datadoghq.com/real_user_monitoring/installation/?tab=us#initialization-parameters
  // for more information on what these individual parameters do.
  datadogRum.init({
    applicationId,
    clientToken,
    service: `${SERVICE_NAME}-ui`,
    env: process.env.NEXT_PUBLIC_VERCEL_ENV,
    version: process.env.NEXT_PUBLIC_COMMIT_SHA,
    // Enables distributed tracing for any `*.zapier.com` or `*.zapier-staging.com` APIs.
    // See: https://docs.datadoghq.com/real_user_monitoring/connect_rum_and_traces/?tab=browserrum
    allowedTracingUrls: [
      {
        match: /https:\/\/(.*\.)?zapier(-staging)?\.com/,
        propagatorTypes: ["datadog"],
      },
    ],
    excludedActivityUrls: [
      (url) =>
        faroCollectorUrl !== undefined && url.startsWith(faroCollectorUrl),
    ],
    beforeSend: (event) =>
      !(
        event.type === "resource" &&
        faroCollectorUrl !== undefined &&
        event.resource.url.startsWith(faroCollectorUrl)
      ),
    sessionSampleRate: 50,
    sessionReplaySampleRate: 10,
    // Force 1% of backend APM traces to be ingested
    traceSampleRate: 1,
    // For the remaining traces, respect the backend service settings
    traceContextInjection: "sampled",
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    allowUntrustedEvents: true,
  });
}

const vitalsMapper: Record<string, string> = {
  // Length of time it takes for the page to start and finish hydrating (in ms)
  "Next.js-hydration": "nextJsHydration",
  // Length of time it takes for a page to start rendering after a route change (in ms)
  "Next.js-route-change-to-render": "nextJsRouteChangeToRender",
  // Length of time it takes for a page to finish render after a route change (in ms)
  "Next.js-render": "nextJsRender",
  // Web Vitals - https://web.dev/vitals/
  CLS: "cumulativeLayoutShift",
  FCP: "firstContentfulPaint",
  FID: "firstInputDelay",
  INP: "interactionToNextPaint",
  LCP: "largestContentfulPaint",
  TTFB: "timeToFirstByte",
};

// Measure and report web vitals that Next.js tracks to Datadog.
// See: https://nextjs.org/docs/advanced-features/measuring-performance
export function nextJsReportWebVitals(metric: NextWebVitalsMetric) {
  const mappedName = vitalsMapper[metric.name] || metric.name;

  if (!shouldTrackToDatadog) {
    console.info(
      `Datadog RUM: would have tracked web vital ${mappedName} - ${metric.value}.`,
    );
    return;
  }

  datadogRum.addAction(`Web Vital: ${mappedName}`, {
    [mappedName]: metric.value,
  });
}

export function datadogRumErrors() {
  if (!shouldTrackToDatadog) {
    console.info("Datadog RUM: error tracking not enabled.");
    return;
  }

  // Log unhandled errors to Datadog
  globalThis.addEventListener("error", (event) => {
    datadogRum.addError(event.error);
  });

  // Log unhandled promise rejections to Datadog
  globalThis.addEventListener("unhandledrejection", (event) => {
    datadogRum.addError(event.reason);
  });
}
